import React, { useLayoutEffect, useRef, useState } from 'react';
import { MarcomPaddingProps } from 'styled-system';
import { JoykitColor } from '@components/themes';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { gsap, Linear, Power1 } from 'gsap';
import { ImgContainerDesktop, ImgContainerMobile, TextContainerDesktop, TextContainerMobile, Wrapper } from './TextBackgroundPhoto.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { defaultBase64Placeholder } from '@components/CloudImage';

type TextBackgroundPhotoProps = Readonly<{
  src: string;
  height: string;
  heightMobile: string;
  children: React.ReactNode;
  gradient?: string;
  textCentered?: boolean;
  moveText?: boolean;
  backgroundHeight?: string;
  backgroundColor?: JoykitColor;
  backgroundPosition?: string;
}> &
  MarcomPaddingProps;

export const TextBackgroundPhoto: React.FC<TextBackgroundPhotoProps> = ({
  src,
  height,
  heightMobile,
  gradient,
  children,
  paddingTop = 0,
  textCentered = false,
  backgroundHeight,
  backgroundColor,
  backgroundPosition,
}) => {
  const [isAppearedInView, setIsAppearedInView] = useState(false);
  const actualSrc = isAppearedInView ? src : defaultBase64Placeholder;
  const textRef = useRef<HTMLDivElement>(null);
  const textBackgroundPhotoContainerRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let textBackgroundPhotoTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      // Initialize timeline
      const initTimeline = () => {
        if (scrollController) {
          textBackgroundPhotoTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          textBackgroundPhotoTimeline.fromTo(
            textBackgroundPhotoContainerRef.current,
            { scale: 1, ease: Power1.easeIn },
            { scale: 1.5, ease: Power1.easeIn }
          );
        }
      };

      // Initialize Scene
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#pin', // TODO: Figure out what's a good id to use here.
          triggerHook: 'onCenter',
          duration: '100%',
          offset: 100,
        })
          .addTo(scrollController)
          // TODO: Set pin based off the value of moveText prop. (use textRef here)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            textBackgroundPhotoTimeline?.progress(event.progress);
          });
      };

      // Call init timeline and init scene
      initTimeline();
      initScene();
    }
    // Clear scene / handle necessary cleanup work
    const clearScene = () => {
      if (textBackgroundPhotoTimeline) {
        textBackgroundPhotoTimeline.progress(0);
        textBackgroundPhotoTimeline.kill();
      }

      if (scene) {
        scene.destroy(true);
      }
    };

    return clearScene;
  }, [scrollController, isMounted]);

  return (
    <Wrapper height={height} heightMobile={heightMobile} paddingTop={paddingTop} textCentered={textCentered} id="pin">
      <LazyLoadWrapper
        beforeLoad={() => {
          setIsAppearedInView(true);
        }}
      >
        <div />
      </LazyLoadWrapper>
      <ImgContainerDesktop
        ref={textBackgroundPhotoContainerRef}
        gradient={gradient}
        src={actualSrc}
        backgroundHeight={backgroundHeight}
        backgroundColor={backgroundColor}
        backgroundPosition={backgroundPosition}
      />
      <TextContainerDesktop ref={textRef}>{children}</TextContainerDesktop>
      <TextContainerMobile ref={textRef}>{children}</TextContainerMobile>
      <ImgContainerMobile
        gradient={gradient}
        src={actualSrc}
        backgroundHeight={backgroundHeight}
        backgroundColor={backgroundColor}
        backgroundPosition={backgroundPosition}
      />
    </Wrapper>
  );
};
